import { LeftOutlined, MenuOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { Link, useLocation } from "react-router-dom";
import { useAuthContext } from "../core/contexts/AuthContextProvider";
import { useMetaContext } from "../core/contexts/MetaContextProvider";
import NavMenu from "../core/navMenu/navMenu";
import UserMenu from "../core/userMenu/userMenu";
import { ROUTES } from "../core/navigation";
import { useToggleState } from "./hooks/useToggleState";

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_WIDTH = 72;

const useStyles = createUseStyles({
  wrap: {
    display: "flex",
    height: "100vh",
    overflow: "hidden",
    background: "#f0f6fb",
  },

  sidebar: {
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    background: "linear-gradient(180deg, #1B2A6B 0%, #162058 60%, #0f1640 100%)",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    position: "relative",
    zIndex: 20,
    flexShrink: 0,
    overflow: "visible",
    transition: "width 0.25s ease, min-width 0.25s ease, transform 0.3s ease",
    "@media (max-width: 1440px)": {
      width: 220,
      minWidth: 220,
    },
  },
  sidebarMobile: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    transform: "translateX(-100%)",
  },
  sidebarMobileOpen: {
    transform: "translateX(0)",
  },
  sidebarNarrow: {
    width: SIDEBAR_COLLAPSED_WIDTH,
    minWidth: SIDEBAR_COLLAPSED_WIDTH,
  },

  logoArea: {
    padding: "0",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    flexShrink: 0,
  },
  logoLink: {
    display: "block",
    textDecoration: "none",
    padding: "0",
  },
  logoWrap: {
    width: "100%",
    padding: "10px 14px",
    background: "linear-gradient(to right, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,0) 100%)",
    borderRadius: 0,
    display: "flex",
    alignItems: "center",
  },
  logoImg: {
    height: "56px",
    width: "auto",
    display: "block",
    maxWidth: "100%",
  },

  logoCollapsedLink: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px 0",
    textDecoration: "none",
  },
  logoCollapsedWrap: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    overflow: "hidden",
    background: "rgba(255,255,255,0.92)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 0 0 2px rgba(255,255,255,0.2)",
  },
  logoCollapsedImg: {
    width: "90%",
    height: "auto",
    objectFit: "contain",
    display: "block",
    margin: "auto",
  },

  collapseBtn: {
    position: "absolute",
    right: "-22px",
    top: "17px",
    width: 22,
    height: 44,
    borderRadius: "0 22px 22px 0",  // flat left, rounded right (D-shape)
    background: "#1B2A6B",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingRight: "4px",
    cursor: "pointer",
    zIndex: 30,
    boxShadow: "4px 0 12px rgba(27,42,107,0.35)",
    fontSize: 12,
    color: "#ffffff",
    padding: 0,
    outline: "none",
    transition: "background 0.15s ease, box-shadow 0.15s ease",
    "& svg": {
      transition: "transform 0.25s ease",
    },
    "&:hover": {
      background: "#243580",
      boxShadow: "4px 0 18px rgba(27,42,107,0.5)",
    },
  },
  collapseBtnRotated: {
    "& svg": {
      transform: "rotate(180deg)",
    },
  },

  underlay: {
    display: "none",
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    zIndex: 19,
  },
  underlayVisible: {
    display: "block",
  },

  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0,
  },

  content: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "24px",
    background: "linear-gradient(135deg, #e8f2fb 0%, #f0f7ff 100%)",
    minHeight: 0,
    "@media (max-height: 900px)": {
      padding: "14px",
    },
    "@media (max-width: 1440px)": {
      padding: "14px",
    },
  },

  mobileTopBar: {
    height: 48,
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    background: "#ffffff",
    borderBottom: "1.5px solid #e8f0f8",
    flexShrink: 0,
    zIndex: 10,
  },

  sidebarUser: {
    flexShrink: 0,
  },
});

const ContentWrapper = ({ children }) => {
  const classes = useStyles();

  const { isAuthenticated } = useAuthContext();
  const { isResponsive } = useMetaContext();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [showNavMenu, toggle] = useToggleState(!isResponsive);
  const location = useLocation();

  useEffect(() => {
    if (isResponsive && isAuthenticated) toggle(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (isResponsive) toggle(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResponsive]);

  if (!isAuthenticated) return children;

  const collapsed = !isResponsive && sidebarCollapsed;

  return (
    <div className={classes.wrap}>
      <aside
        className={`${classes.sidebar}${collapsed ? ` ${classes.sidebarNarrow}` : ""}${isResponsive ? ` ${classes.sidebarMobile}${showNavMenu ? ` ${classes.sidebarMobileOpen}` : ""}` : ""}`}
      >
        <div className={classes.logoArea}>
          {collapsed ? (
            <Link className={classes.logoCollapsedLink} to={ROUTES.dashboard}>
              <div className={classes.logoCollapsedWrap}>
                <img className={classes.logoCollapsedImg} src="/images/logo-transparent.png" alt="Seacare" />
              </div>
            </Link>
          ) : (
            <Link className={classes.logoLink} to={ROUTES.dashboard}>
              <div className={classes.logoWrap}>
                <img className={classes.logoImg} src="/images/logo.png" alt="Seacare Logo" />
              </div>
            </Link>
          )}
        </div>

        {!isResponsive && (
          <button
            className={`${classes.collapseBtn}${collapsed ? ` ${classes.collapseBtnRotated}` : ""}`}
            onClick={() => setSidebarCollapsed((c) => !c)}
          >
            <LeftOutlined />
          </button>
        )}

        <NavMenu collapsed={collapsed} />
        <div className={classes.sidebarUser}>
          <UserMenu sidebar collapsed={collapsed} />
        </div>
      </aside>

      {isResponsive && showNavMenu && (
        <div
          className={`${classes.underlay} ${classes.underlayVisible}`}
          onClick={() => toggle(false)}
        />
      )}

      <div className={classes.main}>
        {isResponsive && (
          <div className={classes.mobileTopBar}>
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => toggle()}
            />
          </div>
        )}
        <main className={classes.content}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default ContentWrapper;
